import { ReactElement, useCallback } from "react";
import { localize } from "client_legacy/ndaw/culture";
import {
	ActionListHeader,
	ActionListItem,
	Toolbar
} from "client/Components";
import { FocalPointEditor } from "./FocalPointEditor";
import {
	Base64FileUriUploadWithMetadataAndAttributionModel,
	fileMetadataSymbol
} from "client/Misc/commonTypes";
import {
	resolveFormattedTextString,
	getDataUrl
} from "client/Misc/utils";
import { getDisplayFriendlySize } from "client_legacy/Documents/fileExtensions";
import styles from "./FocalPointEditorSection.module.less";
import { FocalPoint, FocalPointOptions } from "./FocalPointDialog";
import { AlertManager, useAlerts } from "client/Hooks";
import { formatList } from "client/Misc/cultureSpecificUtils";
import {
	ImageWithAttribution,
	HubImageSelectorKind,
	useHubImageSelector
} from "../Management/useHubImageSelector";
import { ImageAttributionSaveModel } from "client/Api/common";

export type FocalPointEditorSectionProps = {
	uri: string;
	focalPoint: FocalPoint;
	onFocalPointChange: (focalPoint: FocalPoint) => void;
	onImageChange: (image: Base64FileUriUploadWithMetadataAndAttributionModel) => void;
	options: FocalPointOptions;
	isResponsive: boolean;
	onIsBusyChange: (isBusy: boolean) => void;
};

export const FocalPointEditorSection = (props: FocalPointEditorSectionProps): ReactElement => {
	const {
		uri,
		focalPoint,
		onFocalPointChange,
		onImageChange,
		onIsBusyChange,
		isResponsive,
		options
	} = props;

	const {
		maxImageSize,
		supportedImageTypes
	} = options;

	const alertManager: AlertManager = useAlerts();

	const resetFocalPointHandler = useCallback(
		(): void => {
			onFocalPointChange(DEFAULT_FOCAL_POINT);
		},
		[onFocalPointChange]
	);

	const {
		dialogElement: selectImageDialog,
		onSelect: onSelectImage
	} = useHubImageSelector({
		onIsBusyChange,
		supportedImageExtensions: options.canSelectFromOnlineSources
			? options.supportedImageExtensions
			: [],
		defaultImageLocation: options.canSelectFromOnlineSources
			? options.defaultImageLocation
			: null,
		alertManager,
		supportedMimeTypes: supportedImageTypes,
		maxImageSize
	});

	const imageUploadHandler = useCallback(
		async (file: File, attribution: ImageAttributionSaveModel | null): Promise<void> => {
			const selectedImageResult: TryResult<Base64FileUriUploadWithMetadataAndAttributionModel> = await tryHandleImageSelection(
				file,
				attribution,
				alertManager,
				supportedImageTypes,
				maxImageSize
			);

			if (!selectedImageResult.wasSuccessful) {
				return;
			}

			onImageChange(selectedImageResult.result);
		},
		[
			alertManager,
			maxImageSize,
			onImageChange,
			supportedImageTypes
		]
	);

	const selectImageHandler = useCallback(
		async (kind: HubImageSelectorKind): Promise<void> => {
			const result: TryResult<ImageWithAttribution> = await onSelectImage(kind);

			if (!result.wasSuccessful) {
				return;
			}

			const {
				file,
				attribution
			} = result.result;

			await imageUploadHandler(file, attribution);
		},
		[imageUploadHandler, onSelectImage]
	);

	const isDefaultFocalPoint: boolean = focalPoint.left === DEFAULT_FOCAL_POINT.left
		&& focalPoint.top === DEFAULT_FOCAL_POINT.top;

	return (
		<>
			<div className={styles.root}>
				{!isResponsive && (
					<h3>{localize("_SetFocalPoint")}</h3>
				)}

				<div className={styles.editorContainer}>
					<FocalPointEditor
						uri={uri}
						value={focalPoint}
						onChange={onFocalPointChange}
					/>
				</div>
				<div className={styles.footer}>
					<Toolbar>
						<Toolbar.Button
							trackingId="reset"
							text={localize("_Reset")}
							onClick={resetFocalPointHandler}
							kind="secondary"
							isDisabled={isDefaultFocalPoint}
						/>
						{options.canSelectFromOnlineSources
							? (
								<Toolbar.MenuButton
									trackingId="replace-image"
									text={localize("_ReplaceImage")}
									kind="secondary"
									rightIcon="chevron-down"
									responsiveBehavior="always-visible"
								>
									<ActionListHeader
										text={localize("_SelectImage")}
									/>
									<ActionListItem
										icon="folder-open"
										text={localize("_Workspace")}
										trackingId="select-from-workspace"
										onClick={() => selectImageHandler("workspace")}
									/>
									<ActionListItem
										icon="upload"
										text={localize("_Device")}
										trackingId="select-from-device"
										onClick={() => selectImageHandler("device")}
									/>
									{options.isPixabayImageGalleryAvailable && (
										<ActionListItem
											icon="file-image"
											text={localize("_ImageLibraryPixabay")}
											trackingId="select-from-image-library"
											onClick={() => selectImageHandler("pixabay")}
										/>
									)}
								</Toolbar.MenuButton>
							)
							: (
								<Toolbar.Button
									trackingId="replace-image"
									text={localize("_ReplaceImage")}
									onClick={() => selectImageHandler("device")}
									kind="secondary"
									responsiveBehavior="always-visible"
								/>
							)
						}
					</Toolbar>
				</div>
			</div>

			{selectImageDialog}
		</>
	);
};
FocalPointEditorSection.displayName = "FocalPointEditorSection";

export const DEFAULT_FOCAL_POINT: FocalPoint = Object.freeze<FocalPoint>({
	left: 0.5,
	top: 0.5
});

export async function tryHandleImageSelection(
	file: File,
	attribution: ImageAttributionSaveModel | null,
	alertManager: AlertManager,
	supportedImageTypes: readonly string[],
	maxImageSize: number
): Promise<TryResult<Base64FileUriUploadWithMetadataAndAttributionModel>> {
	if (!supportedImageTypes.includes(file.type)) {
		alertManager.showError(
			resolveFormattedTextString({
				template: localize("_SupportedImageFormats"),
				replacements: [
					{
						what: "{0}",
						filler: formatList([
								localize("_FileExtensionJpg"),
								localize("_FileExtensionPng"),
								localize("_FileExtensionGif")
						])
					}
				]
			})
		);

		return {
			wasSuccessful: false
		};
	}

	if (file.size > maxImageSize) {
		alertManager.showError(
			resolveFormattedTextString({
				template: localize("_MaxSizeIs"),
				replacements: [
					{
						what: "{0}",
						filler: getDisplayFriendlySize(maxImageSize)
					}
				]
			})
		);

		return {
			wasSuccessful: false
		};
	}

	const dataUrl: string = await getDataUrl(file);

	return {
		wasSuccessful: true,
		result: {
			filename: file.name,
			uri: dataUrl,
			[fileMetadataSymbol]: {
				size: file.size,
				mimeType: file.type
			},
			attribution
		}
	};
}